<template>
  <div ref="selectRef" class="p-select" :class="classes">
    <div class="p-select__choose">
      <div v-if="multiple" class="p-select__items">
        <p-select-item
          v-for="item in selectedItems"
          :key="item.id"
          :item="item"
          :close="true"
          @close-click="removeItem(item.id)"
          class="p-select-item-active"
        />
        <input
          ref="searchInput"
          class="p-select__search"
          v-model="search"
          :placeholder="isLimitReached ? `Достигнут лимит (${limit})` : placeholder"
          @input="!isLimitReached && (isOpenSelection = true)"
          @focus="!isLimitReached && (isOpenSelection = true)"
          :disabled="isLimitReached"
        >
      </div>

      <p-select-item
        v-else
        :is-icon="isIcon"
        :item="selectedItems[0]"
        @click="isOpenSelection = true"
        class="p-select__item"
      />

      <i class="p-icon p-icon-arrow" @click="!isLimitReached && toggle()"/>
      
      <!-- Tooltip -->
      <popper
        v-if="isTooltip"
        v-bind="tooltipProps"
        class="p-select__popover"
      >
        <i
          class="p-icon p-icon-info"
          :class="{'p-icon-error': !valid && tooltipInfo.length === 0}"
        />
        <template #content>
          <div class="p-popover__content">
            {{ cTooltipInfo }}
          </div>
        </template>
      </popper>
    </div>
    
    <transition name="p-select">
      <div v-if="isOpenSelection" class="p-select__container">
        <div class="p-select__items">
          <p-select-item
            v-for="item in selectionItemsSearched"
            :key="item.id"
            class="p-select__item"
            :class="{'p-select-item-active': isActive(item.id)}"
            :close="close"
            :item="item"
            :is-icon="isIcon"
            @click="() => selectItem(item)"
            @close-click="removeItem(item.id)"
          />
        </div>

        <div v-if="actionButton" class="p-select__action" @click="action">
          <slot name="action"></slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick, onMounted, onBeforeUnmount, PropType } from 'vue';
import Popper from 'vue3-popper';
import pSelectItem from '@/common/pSelect/pSelectItem.vue';

const props = defineProps({
  selectionItems: {
    type: Array as PropType<{ id: number|string; name: string }[]>,
    required: true
  },
  modelValue: {
    type: [String, Number, Array, null] as PropType<null | number | string | (number | string)[]>,
    required: true
  },
  placeholder: {
    type: String,
    default: 'Поиск...'
  },
  multiple: {
    type: Boolean,
    default: false
  },
  isIcon: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  close: {
    type: Boolean,
    default: false
  },
  valid: {
    type: Boolean,
    default: true
  },
  validInfo: {
    type: String,
    default: ''
  },
  isTooltip: {
    type: Boolean,
    default: false
  },
  tooltipProps: {
    type: Object,
    default: () => ({})
  },
  tooltipInfo: {
    type: String,
    default: ''
  },
  limit: {
    type: Number,
    default: 0
  },
  actionButton: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'action', 'removeItem']);

const search = ref<string>('');
const isOpenSelection = ref(false);

const selectRef = ref<HTMLElement | null>(null);
const searchInput = ref<HTMLElement | null>(null);

// TODO: заменить это на блюр
onMounted(() => document.addEventListener('click', handleClickOutside));
onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside));

const handleClickOutside = (event: MouseEvent) => {
  if (selectRef.value && !selectRef.value.contains(event.target as Node)) {
    isOpenSelection.value = false;
  }
};

const action = () => {
  toggle()
  emit('action')
}

const selectedItems = computed(() => {
    if (props.multiple) {
      const itemsMap = new Map(props.selectionItems.map(item => [item.id, item]));
      return (props.modelValue as (number | string)[])
        .map(id => itemsMap.get(id))
        .filter((item): item is { id: number | string; name: string } => item !== undefined);
    }

    return [props.selectionItems.find(item => item.id === props.modelValue) || props.selectionItems[0]];
  }
);

function isActive(itemId: number | string ) {
  return props.multiple ? (props.modelValue as (number | string)[]).includes(itemId) : props.modelValue === itemId
}

const selectionItemsSearched = computed(() => {
  if (!props.multiple) return props.selectionItems
  if (!search.value || search.value.length === 0) return props.selectionItems

  return props.selectionItems.filter(item => item.name.toLowerCase().includes(search.value.toLowerCase()));
});

const classes = computed(() => ({
  'p-select_default': true,
  'p-select_opened': isOpenSelection.value,
  'p-select_disabled': props.disabled,
  'p-select_error': !props.valid,
  'p-select_multiple': props.multiple,
  'p-select_limit-reached': isLimitReached.value
}));

const cTooltipInfo = computed(() => props.tooltipInfo || props.validInfo);

const isLimitReached = computed(() => {
  if (!props.limit || !props.multiple) return false;
  return (props.modelValue as (number | string)[]).length >= props.limit;
});

const toggle = async () => {  
  if (props.disabled) return;
  isOpenSelection.value = !isOpenSelection.value;
  if (props.multiple && isOpenSelection.value) {
    searchInput.value?.focus()
  }
  await nextTick();
};

const selectItem = (item: { id: number | string; name: string }) => {
  if (props.multiple) {
    const selectedArray = props.modelValue as (number | string)[];
    
    if (!selectedArray.includes(item.id) && isLimitReached.value) {
      return;
    }

    emit('update:modelValue', selectedArray.includes(item.id)
      ? selectedArray.filter(id => id !== item.id)
      : [...selectedArray, item.id])
  } else {
    emit('update:modelValue', item.id)
  }
  
  isOpenSelection.value = false
  search.value = ''
};

const removeItem = (itemId: number | string) => {  
  if (props.multiple) {
    emit('update:modelValue', (props.modelValue as (number | string)[]).filter(id => id !== itemId))
  } else {
    emit('removeItem', itemId)
  }
}
</script>

<style>
@import "styles/pSelect.scss";
</style>